import React, { useRef } from 'react';
import { UnorderedList, ListItem } from '@tapresearch/ui/components';
import { useToast as useChakraToast } from '@tapresearch/ui/hooks';
import { UseToastOptions } from '@chakra-ui/react';

export const useToast = () => {
  const _toast = useChakraToast();

  const toast = useRef(
    (
      id: typeof TOASTS[keyof typeof TOASTS] | string,
      {
        status = 'info',
        duration = 4000,
        position = 'top',
        description,
        listItems = [],
        listProps,
        ...rest
      }: UseToastOptions & { listItems?: unknown[]; listProps?: unknown } = {}
    ) => {
      if (!_toast.isActive(id)) {
        _toast({
          id,
          status,
          duration,
          position,
          ...TOAST_DEFAULTS[id],
          description: (
            <>
              {description || TOAST_DEFAULTS[id]?.description}
              {!!listItems.length && (
                <ToastDescriptionList items={listItems} {...listProps} />
              )}
            </>
          ),
          ...rest,
        });
      }
    }
  );

  return { toast: toast.current, TOASTS };
};

const ToastDescriptionList = ({ items, ...rest }: { items: unknown[] }) => (
  <UnorderedList {...rest}>
    {items.map((item, i) => (
      <ListItem key={i}>{item}</ListItem>
    ))}
  </UnorderedList>
);

const TOASTS = {
  PROJECT_NAME_UPDATED: 'PROJECT_NAME_UPDATED',
  PROJECT_INVALID: 'PROJECT_INVALID',
  PROJECT_SETTINGS_SAVED: 'PROJECT_SETTINGS_SAVED',
  INVALID_END_DATE: 'INVALID_END_DATE',
  NUM_RESPONDENTS_ERROR: 'NUM_RESPONDENTS_ERROR',
  ADD_PAYMENT_METHOD_FAILED: 'ADD_PAYMENT_METHOD_FAILED',
  SUBMIT_PAYMENT_FAILED: 'SUBMIT_PAYMENT_FAILED',
  COUNTRY_LANGUAGE_CHANGED: 'COUNTRY_LANGUAGE_CHANGED',
} as const;

const TOAST_DEFAULTS: Record<string, UseToastOptions> = {
  PROJECT_NAME_UPDATED: { title: 'Project name updated.', status: 'success' },
  PROJECT_INVALID: {
    title: 'Errors found on project!',
    duration: 8000,
    isClosable: true,
    status: 'error',
  },
  PROJECT_SETTINGS_SAVED: { status: 'success', duration: 5000 },
  INVALID_END_DATE: {
    status: 'error',
    duration: 3000,
    description:
      "You can't select an end date prior to the next possible launch date.",
  },
  NUM_RESPONDENTS_ERROR: {
    status: 'error',
    description:
      'Unable to update the number of respondents, please try again.',
  },
  ADD_PAYMENT_METHOD_FAILED: {
    status: 'error',
    duration: 3000,
  },
  SUBMIT_PAYMENT_FAILED: {
    status: 'error',
    duration: 3000,
  },
  COUNTRY_LANGUAGE_CHANGED: {
    status: 'success',
    duration: 5000,
    title: 'Project audience updated!',
    description:
      'Your project country/language settings were updated successfully.',
  },
};
